import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CalendarClock, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { useBotSchedulesQuery } from '../../hooks/queries';
import { useScheduleAction } from '../../hooks/mutations';
import { useDateTime } from '../../contexts/DateTimeContext';
import ScheduleForm, { PRESETS, fromPreset } from './ScheduleForm';
import { PanelCard, PanelEmpty, PanelIntro, PanelList, PanelRow, panelButton, panelIconButton, panelPrimary } from './panel/PanelKit';
import { browserTimeZone, cadenceText, formatRun } from './cadence';

export const StatusLine = ({ s, t, lang }) => {
  if (s.last_error === 'credits_exhausted') return <div className="text-amber-700 dark:text-amber-400">{t('bots.schedules.skipped_credits')}</div>;
  if (s.last_error === 'paused') return <div className="text-muted-foreground">{t('bots.schedules.skipped_paused')}</div>;
  if (s.last_status === 'failed') return <div className="text-red-500">{t('bots.schedules.last_failed')}</div>;
  // A watch (backend agents/watch.py): checked without a model, runs only when something changed.
  if (s.watch && (s.last_error || '').startsWith('watch:')) {
    return <div className="text-red-500">{t('bots.schedules.watch_failed', { tool: s.watch.tool, error: s.last_error.slice(7).trim() })}</div>;
  }
  if (s.watch && s.enabled && s.next_run_at) {
    return (
      <div className="text-muted-foreground">
        {t('bots.schedules.next', { when: formatRun(s.next_run_at, lang, s.timezone) })} · {t('bots.cards.watch_text', { tool: s.watch.tool })}
      </div>
    );
  }
  if (s.finished) return <div className="text-muted-foreground">{t('bots.schedules.finished')}</div>;
  if (s.enabled && s.next_run_at) return <div className="text-muted-foreground">{t('bots.schedules.next', { when: formatRun(s.next_run_at, lang, s.timezone) })}</div>;
  return null;
};

/** Work the bot does on its own on a rhythm, in this project. */
const SchedulesPanel = ({ botId, projectId, onOpenChat }) => {
  const { t, i18n } = useTranslation();
  const { timezone } = useDateTime();
  const tz = timezone || browserTimeZone();
  const { data } = useBotSchedulesQuery(botId, projectId);
  const mutation = useScheduleAction();
  const [form, setForm] = useState(null);  // null, or { initial, id } of the form to open
  if (!data) return null;
  const act = (vars, onSuccess) => mutation.mutate({ botId, ...vars }, {
    onSuccess,
    onError: (err) => { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || t('bots.schedules.failed')); },
  });
  const room = data.limit > (data.used ?? data.schedules.length);
  const addButton = room && data.limit > 0 && (
    <Button size="sm" className={panelPrimary} onClick={() => setForm({})} data-testid="schedule-add">
      <Plus className="w-3.5 h-3.5" />{t('bots.schedules.add')}
    </Button>
  );
  return (
    <div className="space-y-5" data-testid="bot-schedules">
      <PanelIntro actions={(
        <>
          <Link to="/app/bots/scheduled" className="text-xs font-medium text-muted-foreground hover:text-foreground px-1">
            {t('bots.scheduled.all')}
          </Link>
          {data.schedules.length > 0 && addButton}
        </>
      )} />
      {data.limit === 0 ? (
        <PanelEmpty icon={CalendarClock}>{t('bots.schedules.pro_only')}</PanelEmpty>
      ) : data.schedules.length === 0 ? (
        <>
          <PanelEmpty icon={CalendarClock} action={addButton}>{t('bots.schedules.empty')}</PanelEmpty>
          <PanelList>
            {PRESETS.map((p) => (
              <PanelRow key={p.key} onClick={() => setForm({ initial: fromPreset(t, p) })}
                icon={<Plus className="w-3.5 h-3.5" />}
                title={<span className="font-medium" data-testid={`schedule-preset-${p.key}`}>{t(`bots.schedules.presets.${p.key}_title`)}</span>}
                subtitle={cadenceText(t, p.cadence, i18n.language)} />
            ))}
          </PanelList>
        </>
      ) : (
        <ul className="space-y-2">
          {data.schedules.map((s) => (
            <PanelCard as="li" key={s.id} className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-foreground truncate" title={s.instruction}>{s.title}</div>
                  <div className="text-xs text-muted-foreground">{cadenceText(t, s.cadence, i18n.language)}</div>
                  <div className="text-xs"><StatusLine s={s} t={t} lang={i18n.language} /></div>
                </div>
                <Switch checked={s.enabled} onCheckedChange={(v) => act({ action: 'update', id: s.id, data: { enabled: v } })} />
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className={panelButton} disabled={mutation.isPending}
                  onClick={() => act({ action: 'runNow', id: s.id }, (r) => { toast.success(t('bots.schedules.started')); onOpenChat?.(r.chat_id); })}>
                  <Play className="w-3.5 h-3.5" />{t('bots.schedules.run_now')}
                </Button>
                <button type="button" className={`${panelIconButton} ml-auto`} disabled={mutation.isPending}
                  title={t('bots.cards.edit')} aria-label={t('bots.cards.edit')} onClick={() => setForm({ id: s.id, initial: s })}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" className={`${panelIconButton} hover:text-red-500`} disabled={mutation.isPending}
                  title={t('common.delete')} aria-label={t('common.delete')} onClick={() => act({ action: 'remove', id: s.id })}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </PanelCard>
          ))}
        </ul>
      )}
      {form && (
        <ScheduleForm open onClose={() => setForm(null)} busy={mutation.isPending} projectId={projectId}
          timezone={form.initial?.timezone || tz} initial={form.initial} editing={!!form.id}
          onSave={(body) => act(form.id
            ? { action: 'update', id: form.id, data: body }
            : { action: 'create', data: { ...body, project_id: projectId } }, () => setForm(null))} />
      )}
    </div>
  );
};

export default SchedulesPanel;
